import { useTranslation } from 'react-i18next';
import { DocumentTitle } from 'components/DocumentTitle';
import { Heading, Paragraph } from 'components/Typography';
import { ContentContainer } from 'components/ContentContainer';
import { Loader } from 'components/Loader';
import { FlashMessage } from 'components/FlashMessages';
import { Link } from 'components/Link';
import { LicenseInfo } from 'domains/licenses/types';

export type Props = {
  licenses: LicenseInfo[];
  isError: boolean;
  isLoading: boolean;
};

export const LicensesUI = ({ licenses, isError, isLoading }: Props): JSX.Element => {
  const { t } = useTranslation();

  if (isError) {
    return (
      <ContentContainer className="min-h-full flex flex-col" variant="full">
        <FlashMessage variant="error" className="mb-64 sm-d:mb-40">
          {t(`licenses.error`)}
        </FlashMessage>
      </ContentContainer>
    );
  }

  if (isLoading) {
    return (
      <Loader data-testid="licenses-loader" className="flex grow items-center justify-center p-24 text-primary-500" />
    );
  }

  return (
    <>
      <DocumentTitle>{t('licenses.pageTitle')}</DocumentTitle>
      <ContentContainer className="bg-cream-50 min-h-full flex flex-col" variant="full">
        <Heading level="h2" as="h3" className="mb-32 md:mb-32">
          {t('licenses.pageTitle')}
        </Heading>
        <div>
          <div className="ml-9">
            {licenses.length === 0 ? (
              <ContentContainer className="min-h-full flex flex-col" variant="full">
                <FlashMessage variant="info" className="mb-64 sm-d:mb-40">
                  {t(`licenses.error`)}
                </FlashMessage>
              </ContentContainer>
            ) : (
              <div className="mx-4 mt-4">
                <ul className="list-none pl-5 space-y-4">
                  {licenses.map((license, index) => (
                    <li key={index} className="py-10">
                      <Paragraph gutterBottom={false} className="font-semibold">
                        {license.name}
                      </Paragraph>
                      <Paragraph gutterBottom={false}>{license.licenseType}</Paragraph>
                      <Paragraph gutterBottom={false}>
                        <Link
                          className="inline-flex"
                          key="license-link"
                          to={'https://www.apache.org/licenses/LICENSE-2.0'}
                          disableExternalLinkIcon
                          includeDefaultClasses
                        >
                          {t('licenses.link')}
                        </Link>
                      </Paragraph>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ContentContainer>
    </>
  );
};
